"use client";

import { useState } from "react";
import Link from "next/link";
import { Copy, Users, Zap, LineChart, ArrowRight } from "lucide-react";
import { useT } from "@/hooks/useT";

/**
 * Product Features — 4 tabs, one showcased at a time.
 *
 * Replaces CopyTradingSection + TradingTools + PlatformFeatures + HowItWorks.
 * The old layout listed every feature as a full-screen block; this compresses
 * the narrative into 4 clean pillars with a single visual slot that swaps
 * by selected tab. Less scroll, more impact.
 *
 * Tabs:
 *   1. Copytrading — mirror top traders the second they open a position
 *   2. Social      — posts, feed, signals from the community
 *   3. Execution   — self-custody, <1s routing, multi-chain
 *   4. Tracking    — whale alerts, smart money, PnL analytics
 */

type TabId = "copy" | "social" | "exec" | "track";

interface Tab {
  id: TabId;
  eyebrow: string;
  title: string;
  body: string;
  bullets: string[];
  href: string;
  cta: string;
  icon: React.ComponentType<{ className?: string }>;
}

/** Static config: identifiers + URLs + icons stay in EN.
 *  Localised copy is resolved at render time via the i18n catalog. */
const TABS: Array<{ id: TabId; href: string; icon: Tab["icon"]; keyPrefix: string }> = [
  { id: "copy", href: "/copytrading", icon: Copy, keyPrefix: "home.features.copy" },
  { id: "social", href: "/activity", icon: Users, keyPrefix: "home.features.social" },
  { id: "exec", href: "/markets", icon: Zap, keyPrefix: "home.features.exec" },
  { id: "track", href: "/activity", icon: LineChart, keyPrefix: "home.features.track" },
];

export default function ProductFeatures() {
  const t = useT();
  const [active, setActive] = useState<TabId>("copy");
  const tab = TABS.find((x) => x.id === active)!;
  const title = t(`${tab.keyPrefix}.title`);

  return (
    <section className="py-20 lg:py-28 bg-white dark:bg-[#0a0a0b]">
      <div className="mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-10">
        {/* Header */}
        <div className="max-w-3xl mb-10 lg:mb-14">
          <div className="font-inter-tight text-[11px] font-semibold uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-3">
            {t("home.features.eyebrow")}
          </div>
          <h2 className="font-inter-tight font-semibold text-gray-900 dark:text-white tracking-[-0.02em] text-[34px] lg:text-[48px] leading-[1.05]">
            {t("home.features.title")}
          </h2>
          <p className="mt-3 text-gray-600 dark:text-gray-400 font-inter-tight text-base lg:text-lg">
            {t("home.features.body")}
          </p>
        </div>

        {/* Tabs row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 lg:gap-3 mb-10">
          {TABS.map((x) => {
            const isActive = x.id === active;
            const Icon = x.icon;
            const xTitle = t(`${x.keyPrefix}.title`);
            return (
              <button
                key={x.id}
                type="button"
                onClick={() => setActive(x.id)}
                className={`group text-left rounded-[5px] border p-4 transition-colors ${
                  isActive
                    ? "border-gray-900 dark:border-white bg-gray-900 dark:bg-white text-white dark:text-gray-900"
                    : "border-gray-200 dark:border-[#1A1A1A] bg-white dark:bg-[#18181D] text-gray-900 dark:text-white hover:border-gray-300 dark:hover:border-white/20"
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-4 h-4" />
                  <span className="font-inter-tight text-[10px] uppercase tracking-widest opacity-70">
                    {t(`${x.keyPrefix}.eyebrow`)}
                  </span>
                </div>
                <div
                  className={`font-inter-tight text-sm font-medium leading-snug ${
                    isActive ? "opacity-100" : "opacity-80"
                  }`}
                >
                  {xTitle.split(".")[0]}.
                </div>
              </button>
            );
          })}
        </div>

        {/* Active tab panel */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-8 lg:gap-12 items-start">
          {/* Copy side */}
          <div className="lg:pt-6">
            <h3 className="font-inter-tight font-semibold text-gray-900 dark:text-white tracking-[-0.015em] text-[26px] lg:text-[34px] leading-tight">
              {title}
            </h3>
            <p className="mt-4 text-gray-600 dark:text-gray-400 font-inter-tight text-base lg:text-lg leading-relaxed">
              {t(`${tab.keyPrefix}.body`)}
            </p>
            <ul className="mt-6 space-y-2.5">
              {[1, 2, 3].map((n) => (
                <li
                  key={n}
                  className="flex items-start gap-2.5 font-inter-tight text-[15px] text-gray-700 dark:text-gray-300"
                >
                  <span className="mt-2 w-1 h-1 rounded-full bg-gray-400 dark:bg-gray-500 flex-shrink-0" />
                  <span>{t(`${tab.keyPrefix}.bullet${n}`)}</span>
                </li>
              ))}
            </ul>
            <Link
              href={tab.href}
              className="mt-8 inline-flex items-center gap-2 px-5 py-3 rounded-[5px] bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-inter-tight font-medium text-sm hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
            >
              {t(`${tab.keyPrefix}.cta`)}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Visual slot — each tab gets its own visual */}
          <ProductVisual activeId={tab.id} />
        </div>
      </div>
    </section>
  );
}

/**
 * Minimalist visual panels per feature. Instead of a fake screenshot each
 * tab shows a schematic of that feature — keeps the page light and doesn't
 * require image assets that rot when the UI evolves.
 */
function ProductVisual({ activeId }: { activeId: TabId }) {
  return (
    <div className="relative rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] bg-gray-50 dark:bg-[#18181D] p-6 lg:p-8 min-h-[360px] overflow-hidden">
      {activeId === "copy" && <CopyVisual />}
      {activeId === "social" && <SocialVisual />}
      {activeId === "exec" && <ExecVisual />}
      {activeId === "track" && <TrackVisual />}
    </div>
  );
}

function CopyVisual() {
  const t = useT();
  return (
    <div className="relative h-full flex flex-col justify-center gap-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-[5px] bg-gray-900 dark:bg-white" />
        <div>
          <div className="font-inter-tight text-sm font-semibold text-gray-900 dark:text-white">
            @alphafren
          </div>
          <div className="font-inter-tight text-[11px] text-gray-500 dark:text-gray-400">
            {t("home.visual.copy.topLeader")}
          </div>
        </div>
        <span className="ml-auto font-inter-tight text-[11px] font-semibold uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
          +$42.1K 30d
        </span>
      </div>

      <div className="rounded-[5px] bg-white dark:bg-[#0a0a0b] border border-gray-200 dark:border-white/10 p-4">
        <div className="font-inter-tight text-[11px] uppercase tracking-widest text-gray-500 dark:text-gray-400 mb-2">
          {t("home.visual.copy.justOpened")}
        </div>
        <div className="font-inter-tight text-sm text-gray-900 dark:text-white mb-3">
          {t("home.visual.copy.bought")} <span className="font-semibold text-emerald-600 dark:text-emerald-400">YES</span> {t("home.visual.copy.boughtSuffix")}
        </div>
        <div className="flex items-center justify-between">
          <span className="font-inter-tight text-[11px] text-gray-500 dark:text-gray-400">
            {t("home.visual.copy.mirroring")}
          </span>
          <span className="font-inter-tight text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
            ~620ms avg
          </span>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 animate-ping" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
        </span>
        <span className="font-inter-tight text-[11px] text-gray-500 dark:text-gray-400">
          {t("home.visual.copy.executed")}
        </span>
      </div>
    </div>
  );
}

function SocialVisual() {
  const t = useT();
  return (
    <div className="h-full flex flex-col gap-3">
      {[
        {
          who: "@elon",
          handle: t("home.visual.social.followers"),
          body: t("home.visual.social.elonBody"),
          pnl: "+12%",
        },
        {
          who: "whale.eth",
          handle: t("home.visual.social.verifiedTrade"),
          body: t("home.visual.social.whaleBody"),
          pnl: t("home.card.tier.whale"),
        },
        {
          who: "@alphafren",
          handle: t("home.visual.social.topLeader"),
          body: t("home.visual.social.threadBody"),
          pnl: t("home.visual.social.insight"),
        },
      ].map((p, i) => (
        <div
          key={i}
          className="rounded-[5px] border border-gray-200 dark:border-white/10 bg-white dark:bg-[#0a0a0b] p-3"
        >
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-6 h-6 rounded-[5px] bg-gray-200 dark:bg-white/10" />
            <span className="font-inter-tight text-[13px] font-semibold text-gray-900 dark:text-white">
              {p.who}
            </span>
            <span className="font-inter-tight text-[11px] text-gray-500 dark:text-gray-400">
              · {p.handle}
            </span>
            <span className="ml-auto font-inter-tight text-[10px] font-medium uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
              {p.pnl}
            </span>
          </div>
          <div className="font-inter-tight text-[13px] text-gray-700 dark:text-gray-300">{p.body}</div>
        </div>
      ))}
    </div>
  );
}

function ExecVisual() {
  const t = useT();
  const venues = [
    { name: "Polymarket", chain: "Polygon", color: "#2962FF" },
    { name: "Kalshi", chain: "Solana (DFlow)", color: "#14F195" },
    { name: "Hyperliquid", chain: "HyperCore (HIP-4)", color: "#97FCE4" },
  ];
  return (
    <div className="h-full flex flex-col gap-4 justify-center">
      <div className="font-inter-tight text-[11px] uppercase tracking-widest text-gray-500 dark:text-gray-400">
        {t("home.visual.exec.tagline")}
      </div>
      {venues.map((v) => (
        <div
          key={v.name}
          className="flex items-center gap-3 rounded-[5px] border border-gray-200 dark:border-white/10 bg-white dark:bg-[#0a0a0b] px-4 py-3"
        >
          <span
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: v.color }}
          />
          <div className="flex-1 min-w-0">
            <div className="font-inter-tight text-sm font-semibold text-gray-900 dark:text-white">
              {v.name}
            </div>
            <div className="font-inter-tight text-[11px] text-gray-500 dark:text-gray-400">
              {v.chain}
            </div>
          </div>
          <span className="font-inter-tight text-[11px] font-semibold uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
            {t("home.visual.exec.connected")}
          </span>
        </div>
      ))}
      <div className="mt-2 text-center font-inter-tight text-[11px] text-gray-500 dark:text-gray-400">
        <span className="font-semibold text-gray-700 dark:text-gray-300">~620ms</span> {t("home.visual.exec.endToEnd")}
      </div>
    </div>
  );
}

function TrackVisual() {
  const t = useT();
  return (
    <div className="h-full flex flex-col justify-center gap-3">
      {[
        { label: t("home.visual.track.whaleAlert"), body: t("home.visual.track.whaleBody"), color: "#FF1744" },
        { label: t("home.visual.track.smartMoney"), body: t("home.visual.track.smartBody"), color: "#00C853" },
        { label: t("home.visual.track.priceAlert"), body: t("home.visual.track.priceBody"), color: "#7C3AED" },
        { label: t("home.visual.track.pnl"), body: t("home.visual.track.pnlBody"), color: "#2962FF" },
      ].map((r, i) => (
        <div
          key={i}
          className="flex items-center gap-3 rounded-[5px] border border-gray-200 dark:border-white/10 bg-white dark:bg-[#0a0a0b] px-4 py-3"
        >
          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: r.color }} />
          <div className="min-w-0">
            <div className="font-inter-tight text-[10px] uppercase tracking-widest text-gray-500 dark:text-gray-400">
              {r.label}
            </div>
            <div className="font-inter-tight text-sm text-gray-900 dark:text-white truncate">
              {r.body}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
